import type { Advisor } from '@ria-client/advisor-search/types';
import { feeLabel, formatAum, getYearsRegistered } from '@ria-client/advisor-search/utils';

const EM_DASH = '—';

export interface HeaderStat {
  label: string;
  value: string;
  tooltip: string;
}

export interface ExpandedStat {
  label: string;
  value: string;
}

export function formatAddressLine(advisor: Advisor): string {
  const cityState = `${advisor.city}, ${advisor.state} ${advisor.zip}`;
  return advisor.street ? `${advisor.street}, ${cityState}` : cityState;
}

export function formatRegistrationDate(regDate: string): string {
  const [year, month, day] = regDate.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function getHeaderStats(advisor: Advisor): HeaderStat[] {
  const yearsRegistered = advisor.regDate ? getYearsRegistered(advisor.regDate) : null;

  return [
    {
      label: 'Clients',
      value: advisor.clients != null ? advisor.clients.toLocaleString() : EM_DASH,
      tooltip: 'Number of client accounts managed (from latest Form ADV).',
    },
    {
      label: 'AUM',
      value: advisor.aum != null ? formatAum(advisor.aum) : EM_DASH,
      tooltip: 'Assets Under Management — total client assets the firm advises on.',
    },
    {
      label: 'Firm Age',
      value: yearsRegistered != null ? `${yearsRegistered} years` : EM_DASH,
      tooltip: 'Years since the firm registered with the SEC or state regulator.',
    },
    {
      label: 'Compensation',
      value: feeLabel(advisor.fees),
      tooltip:
        'How the advisor charges: AUM %, flat fee, hourly, commission, or performance-based.',
    },
  ];
}

export function getExpandedStats(advisor: Advisor): ExpandedStat[] {
  const stats: ExpandedStat[] = [{ label: 'CRD Number', value: advisor.crd }];
  if (advisor.regDate) {
    stats.push({ label: 'Originally Registered', value: formatRegistrationDate(advisor.regDate) });
  }
  stats.push({ label: 'Registration', value: advisor.regStatus });
  return stats;
}
